/* eslint-disable lingui/no-unlocalized-strings -- intentionally not translated */
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import Avatar from '../ui/Avatar';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Popover from '../ui/Popover';
import Icon from '../ui/Icon';
import ButtonIcon from '../ui/ButtonIcon';
import { useCurrentUser } from '../../services/userHooks';
import ThemeSelector from '../DisplaySettings/ThemeSelector';
import DisplayOptions from '../DisplaySettings/DisplayOptions';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { getListPathFromPathname } from '../../utils/readerRouteUtils';
import { handleLogout } from '../../utils/logout';
import { useKeyboardShortcutsModal } from '../KeyboardShortcutsModal/KeyboardShortcutsContext';

function DesktopFooterNavLinks() {
  const { currentUser } = useCurrentUser();
  const { preferences, color, useSystemColor, handlers } = useDisplaySettings();
  const { readerLayoutMode, setReaderLayoutMode, setReaderBookmarkId } =
    useUserPreferences();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const { openShortcutsModal } = useKeyboardShortcutsModal();

  // Focus the "Back" button when switching to the "More" submenu
  useEffect(() => {
    if (showMoreMenu) {
      backButtonRef.current?.focus();
    }
  }, [showMoreMenu]);

  // Get display name with fallback: display_name -> username -> first initial of email
  const displayName = (() => {
    // Use display_name from currentUser (now included in to_website_hash)
    if (currentUser?.display_name?.trim()) {
      return currentUser.display_name.trim();
    }

    // Fallback to username
    if (currentUser?.username) {
      return currentUser.username;
    }

    // Fallback to first initial of email
    if (currentUser?.email) {
      const firstChar = currentUser.email.charAt(0).toUpperCase();
      return firstChar;
    }

    return t`Profile`;
  })();

  return (
    <div className="side-item-footer">
      <div className="side-nav-footer-inner">
        <div className="side-nav-footer-profile">
          <Avatar
            avatarUrl={currentUser?.profile_picture_url}
            displayName={currentUser?.display_name}
            username={currentUser?.username}
            className="side-nav-footer-avatar"
            alt={displayName}
          />
          <span className="side-nav-footer-name">{displayName}</span>
        </div>
        <div className="side-nav-section-profile-more flex-none">
          <Popover
            placement="right-end"
            offset={{ mainAxis: 16, crossAxis: 5 }}
            role="menu"
            className="animate-[fadeIn_0.15s_ease] !shadow-none"
            onOpenChange={(open) => {
              if (!open) setShowMoreMenu(false);
            }}
            content={
              <div className="min-w-[200px]">
                {showMoreMenu ? (
                  <>
                    {/* Back button */}
                    <button
                      ref={backButtonRef}
                      onClick={() => setShowMoreMenu(false)}
                      className="menu-item px-2 transition-colors hover:bg-[var(--state-nav-hover)]"
                    >
                      <Icon name="arrow-left" />
                      <Trans>Back</Trans>
                    </button>
                    <div className="menu-divider"></div>

                    {/* More menu links */}
                    <a href="/apps" className="menu-item">
                      Apps
                    </a>
                    <a href="/save" className="menu-item">
                      How to Save
                    </a>
                    <a href="/premium" className="menu-item">
                      Premium
                    </a>
                    <a href="/api" className="menu-item">
                      Developers
                    </a>
                    <a href="/press" className="menu-item">
                      Press
                    </a>
                    <a href="/publishers" className="menu-item">
                      Publishers
                    </a>
                    <a href="/terms" className="menu-item">
                      Privacy &amp; Terms
                    </a>
                    <div className="menu-divider"></div>
                    <a
                      href="http://twitter.com/instapaper"
                      target="_blank"
                      rel="noreferrer"
                      className="menu-item"
                    >
                      Twitter
                    </a>
                    <a
                      href="https://www.facebook.com/instapaperholdings"
                      target="_blank"
                      rel="noreferrer"
                      className="menu-item"
                    >
                      Facebook
                    </a>
                  </>
                ) : (
                  <>
                    {/* Appearance Section */}
                    <div className="flex flex-col gap-3 px-3 py-2">
                      <div>
                        <div className="text-copy-secondary mb-1 text-xs">
                          <Trans>Appearance</Trans>
                        </div>

                        {/* Color Theme Swatches */}
                        <ThemeSelector
                          currentColor={color}
                          useSystemColor={useSystemColor}
                          onColorChange={handlers.handleColorChange}
                          onSystemColorChange={handlers.handleSystemColorToggle}
                        />
                      </div>

                      {/* Condensed, Thumbnails, and Reader Options */}
                      <div>
                        <DisplayOptions
                          preferences={preferences}
                          onCondensedViewToggle={handlers.handleCondensedViewToggle}
                          onThumbnailsToggle={handlers.handleThumbnailsToggle}
                          onReaderToggle={(checked) => {
                            if (checked) {
                              setReaderLayoutMode('3col');
                            } else {
                              setReaderLayoutMode('2col');
                              setReaderBookmarkId(null);
                              if (pathname.includes('/read/')) {
                                const listPath = getListPathFromPathname(pathname);
                                navigate({
                                  to: listPath as '/',
                                  replace: true,
                                });
                              }
                            }
                          }}
                          readerEnabled={readerLayoutMode === '3col'}
                          className="flex flex-col gap-3"
                        />
                      </div>
                    </div>

                    <div className="menu-divider"></div>

                    <Link
                      to="/p/$id"
                      params={{ id: currentUser?.username || currentUser?.id || '' }}
                      className="menu-item"
                    >
                      <Trans>Profile</Trans>
                    </Link>

                    <Link to="/settings/account" className="menu-item">
                      <Trans>Settings</Trans>
                    </Link>

                    <button onClick={openShortcutsModal} className="menu-item">
                      <Trans>Keyboard shortcuts</Trans>
                    </button>

                    <button
                      onClick={() => {
                        if (currentUser?.formKey) {
                          handleLogout(currentUser.formKey);
                        }
                      }}
                      className="menu-item"
                    >
                      <Trans>Sign Out</Trans>
                    </button>

                    <div className="menu-divider"></div>

                    <a
                      href="http://blog.instapaper.com"
                      target="_blank"
                      rel="noreferrer"
                      className="menu-item"
                    >
                      Blog
                    </a>

                    <Link to="/docs" className="menu-item">
                      Docs
                    </Link>

                    <button onClick={() => setShowMoreMenu(true)} className="menu-item">
                      <Trans>More</Trans>
                    </button>
                  </>
                )}
              </div>
            }
          >
            <ButtonIcon icon="more" title={t`Open menu`} iconClassName="rotate-90" />
          </Popover>
        </div>
      </div>
    </div>
  );
}

export default DesktopFooterNavLinks;
